<template>
  <div>
    <!--
      STOCKCREW 配送标下载弹窗。
      点击遮罩层不允许关闭，避免用户在文件下载过程中误触导致选择丢失。
    -->
    <el-dialog custom-class="outer-label-dialog" :close-on-click-modal="false"
      :visible.sync="dialogVisible" width="480px">
      <!-- 自定义标题：与普通业务弹窗区分，突出当前操作会下载 PDF 文件。 -->
      <div slot="title" class="dialog-title">
        <i class="el-icon-document"></i>
        <span>{{ $t('message.outerLabelDialogTitle') }}</span>
      </div>
      <!-- 标签排版格式说明。 -->
      <div class="format-tip">{{ $t('message.outerLabelFormatTip') }}</div>
      <!--
        配送标支持两种后台约定的 labelFormat：
        1. a4_6up_105x99：A4 一页六张，需要额外选择首页开始位置；
        2. single_100x150：单张热转印标签，不传 startPosition。
      -->
      <div class="label-format-options">
        <!-- A4 六面标签卡片：整张卡片均可点击，active 仅控制选中样式。 -->
        <div class="format-card" :class="{ active: labelFormat === 'a4_6up_105x99' }"
          @click="labelFormat = 'a4_6up_105x99'">
          <el-radio v-model="labelFormat" label="a4_6up_105x99" class="format-radio">
            <strong>{{ $t('message.outerLabelA4Title') }}</strong>
            <span class="format-size">105 × 99 mm</span>
          </el-radio>
          <div class="format-description">
            <i class="el-icon-menu"></i>
            <span>{{ $t('message.outerLabelA4Description') }}</span>
          </div>
        </div>
        <!-- 100×150 单张标签卡片。 -->
        <div class="format-card" :class="{ active: labelFormat === 'single_100x150' }"
          @click="labelFormat = 'single_100x150'">
          <el-radio v-model="labelFormat" label="single_100x150" class="format-radio">
            <strong>{{ $t('message.outerLabelSingleTitle') }}</strong>
            <span class="format-size">100 × 150 mm</span>
          </el-radio>
          <div class="format-description single-description">
            <i class="single-page-icon"></i>
            <span>{{ $t('message.outerLabelSingleDescription') }}</span>
          </div>
        </div>
      </div>
      <!--
        105×99 为 A4 六面标签，首页可能已有部分标签被使用，
        因此必须指定从 1～6 中的哪一面开始打印；单张格式不展示此区域。
      -->
      <div class="start-position" v-if="labelFormat === 'a4_6up_105x99'">
        <div class="start-position-title">
          <i class="el-icon-time"></i>
          <span>{{ $t('message.outerLabelStartPosition') }}</span>
        </div>
        <div class="start-position-content">
          <!-- 六个按钮按纸张上的 2 列 × 3 行位置排列，按钮值直接作为 startPosition。 -->
          <div class="position-grid">
            <button v-for="position in 6" :key="position" type="button"
              :class="{ active: startPosition === position }" @click="startPosition = position">
              {{ position }}
            </button>
          </div>
          <!-- 当前选中面提示，方便用户在提交前再次确认。 -->
          <div class="current-position">
            <i></i>
            <span>{{ $t('message.outerLabelSelectedPosition', { position: startPosition }) }}</span>
          </div>
        </div>
      </div>
      <!-- 下载期间确认按钮进入 loading 状态，download 方法同时阻止重复请求。 -->
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('message.cancel') }}</el-button>
        <el-button type="primary" class="confirm-download" :loading="downloading" @click="download">
          {{ $t('message.outerLabelConfirmDownload') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // 控制弹窗显示状态，由配送单列表通过 ref 调用 open 方法打开。
      dialogVisible: false,
      // 下载请求锁：同时用于按钮 loading 和防止连续点击重复下载。
      downloading: false,
      // 当前配送单号，对应接口参数 porder_sn，并用于生成下载文件名。
      porderSn: '',
      // 后台约定的标签排版格式，默认使用 A4 一页六张。
      labelFormat: 'a4_6up_105x99',
      // A4 首页开始打印面，合法范围为 1～6；单张格式不会提交该字段。
      startPosition: 1
    }
  },
  methods: {
    /**
     * 打开下载弹窗。
     * 每次都重置为 A4 六面、从第 1 面开始，避免切换配送单后沿用上一次选择。
     * @param {String} porderSn 配送单号
     */
    open(porderSn) {
      this.porderSn = porderSn
      this.labelFormat = 'a4_6up_105x99'
      this.startPosition = 1
      this.dialogVisible = true
    },
    /**
     * 根据当前选择请求配送标 PDF 文件流并触发浏览器下载。
     * 接口参数约束：
     * - porder_sn：配送单号；
     * - labelFormat：a4_6up_105x99 或 single_100x150；
     * - startPosition：仅 A4 六面格式提交，且必须为字符串 1～6。
     */
    download() {
      // 请求尚未结束时直接返回，防止重复生成和重复下载。
      if (this.downloading) return

      // 两种格式都必须提交配送单号和标签格式。
      const params = {
        porder_sn: this.porderSn,
        labelFormat: this.labelFormat
      }

      // A4 六面格式额外校验首页开始打印位置，单张格式不携带该参数。
      if (this.labelFormat === 'a4_6up_105x99') {
        const startPosition = Number(this.startPosition)
        if (!Number.isInteger(startPosition) || startPosition < 1 || startPosition > 6) {
          return this.$message.error(this.$t('message.outerLabelStartPositionRequired'))
        }
        params.startPosition = String(startPosition)
      }

      this.downloading = true
      this.$api.porderApi.getOuterLabelPdf(params).then(async (res) => {
        // 文件流接口的业务错误同样以 Blob 返回，code 10000 时只提示，不触发下载。
        const contentType = (res.headers && res.headers['content-type']) || res.data.type || ''
        if (contentType.includes('application/json')) {
          const responseData = JSON.parse(await res.data.text())
          if (Number(responseData.code) === 10000) {
            this.$message.error(responseData.msg)
            return
          }
        }
        // 接口返回 PDF 文件流，将响应内容转换为浏览器可下载的 Blob。
        const blob = new Blob([res.data], { type: 'application/pdf' })
        // 为 Blob 创建临时 URL，再通过隐藏的 a 标签触发原生文件下载。
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `配送标_${this.porderSn}.pdf`
        document.body.appendChild(link)
        link.click()
        // 下载动作触发后立即释放临时 URL 和 DOM，避免多次下载造成内存残留。
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        // 下载成功后关闭弹窗；失败时保留弹窗，方便用户直接重试。
        this.dialogVisible = false
      }).catch(() => {
        // 文件流请求失败时统一展示业务错误提示。
        this.$message.error(this.$t('message.productLabelDownloadFailed'))
      }).finally(() => {
        // 无论成功或失败都解除下载锁。
        this.downloading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// 覆盖 Element UI 弹窗外壳：统一圆角、阴影和头部/内容/底部间距。
/deep/ .outer-label-dialog {
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 18px 45px rgba(31, 45, 61, 0.18);

  .el-dialog__header {
    padding: 24px 20px 8px;
  }

  .el-dialog__headerbtn {
    top: 23px;
    right: 20px;
    font-size: 20px;
  }

  .el-dialog__body {
    padding: 0 20px;
    color: #303133;
  }

  .el-dialog__footer {
    padding: 14px 20px 18px;
  }
}

// 弹窗标题及 PDF 图标。
.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1f2329;
  font-size: 20px;
  font-weight: 600;

  i {
    color: #c52932;
    font-size: 20px;
  }
}

// 标题下方的格式选择引导及分隔线。
.format-tip {
  padding-bottom: 12px;
  border-bottom: 1px solid #dcdfe6;
  color: #909399;
  line-height: 20px;
}

// 两种排版格式纵向排列，避免说明文字过长时相互挤压。
.label-format-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 0 12px;
}

// 标签格式卡片：active 状态与单选框保持同一个 labelFormat 数据源。
.format-card {
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background-color: #fff;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #d77a80;
  }

  &.active {
    padding: 9px 11px;
    border: 2px solid #c52932;
    box-shadow: 0 0 0 2px rgba(197, 41, 50, 0.06);
  }

  /deep/ .format-radio {
    display: flex;
    align-items: center;
    margin-right: 0;
    color: #303133;

    .el-radio__input.is-checked .el-radio__inner {
      border-color: #c52932;
      background-color: #c52932;
    }

    .el-radio__label {
      display: flex;
      flex: 1;
      align-items: center;
      gap: 8px;
      padding-left: 8px;
      color: #303133;
      font-size: 15px;
    }
  }

  .format-size {
    padding: 1px 8px;
    border: 1px solid #dcdfe6;
    border-radius: 10px;
    color: #606266;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }

  .format-description {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0 0 29px;
    color: #7f828b;
    font-size: 13px;
    line-height: 18px;

    .el-icon-menu {
      color: #909399;
      font-size: 13px;
    }
  }

  .single-description {
    align-items: flex-start;

    .single-page-icon {
      flex: none;
      width: 8px;
      height: 11px;
      margin-top: 3px;
      background-color: #a8abb2;
    }
  }
}

// A4 六面标签的首页起始位置选择面板。
.start-position {
  padding: 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background-color: #fff;

  .start-position-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #303133;
    font-weight: 600;

    i {
      color: #c52932;
      font-size: 14px;
    }
  }

  .start-position-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding-top: 12px;
  }

  .position-grid {
    display: grid;
    grid-template-columns: repeat(2, 54px);
    gap: 4px;
    padding: 4px;
    border: 1px solid #cfd2d8;
    border-radius: 6px;

    button {
      width: 54px;
      height: 52px;
      padding: 0;
      border: 1px solid #dcdfe6;
      border-radius: 5px;
      outline: none;
      background-color: #f7f8fa;
      color: #7f828b;
      cursor: pointer;
      transition: color 0.2s, border-color 0.2s, background-color 0.2s;

      &:hover {
        color: #c52932;
        border-color: #d77a80;
      }

      &.active {
        color: #fff;
        border-color: #c52932;
        background-color: #c52932;
      }
    }
  }

  .current-position {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 145px;
    color: #606266;

    i {
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background-color: #c52932;
    }
  }
}

// 底部操作区：确认按钮使用系统业务红色，loading 由 downloading 控制。
.dialog-footer {
  .el-button {
    min-width: 72px;
  }

  .confirm-download {
    border-color: #b8272d;
    background-color: #b8272d;

    &:hover,
    &:focus {
      border-color: #c94d52;
      background-color: #c94d52;
    }
  }
}
</style>
